import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Typography,
  Flex,
  Card,
  Space,
} from 'antd';
import { FiArrowRight } from 'react-icons/fi';
import {
  ArrowLeftOutlined,
  FileTextOutlined,
  FileDoneOutlined,
} from '@ant-design/icons';
import customerService from '../../services/customerService';
import WorkspaceEventsPanel, { workspaceEventSubscribeNoOp } from '../../components/workspaces/WorkspaceEventsPanel';
import PageShell from '../../components/common/PageShell';

const { Text } = Typography;

/**
 * Full-page workspace timeline for a single invoice, estimate, or workspace.
 * Route: /workspaces/invoice/:publicId
 * Route: /workspaces/estimate/:publicId
 * Route: /workspaces/workspace/:publicId
 */
const WorkspaceDetailPage = () => {
  const { documentType, publicId } = useParams();
  const navigate = useNavigate();

  const variant = documentType === 'invoice'
    ? 'invoice'
    : documentType === 'estimate'
      ? 'estimate'
      : documentType === 'workspace'
        ? 'workspace'
        : null;

  const [entityData, setEntityData] = useState(null);
  const [entityLoading, setEntityLoading] = useState(true);

  useEffect(() => {
    setEntityLoading(true);
    setEntityData(null);
    if (!variant || !publicId) {
      setEntityLoading(false);
      return;
    }
    const fetch =
      variant === 'invoice'   ? customerService.getInvoice(publicId)   :
      variant === 'estimate'  ? customerService.getEstimate(publicId)  :
                                customerService.getWorkspace(publicId);
    fetch
      .then((data) => setEntityData(data))
      .catch(() => {})
      .finally(() => setEntityLoading(false));
  }, [variant, publicId]);

  const documentLabel =
    variant === 'invoice'  ? 'Invoice'   :
    variant === 'estimate' ? 'Estimate'  :
    variant === 'workspace' ? 'Workspace' :
    'Document';

  const summarySlot = useMemo(() => {
    if (variant === 'workspace') {
      const invoices  = entityData?.invoices  ?? [];
      const estimates = entityData?.estimates ?? [];
      const hasLinked = invoices.length > 0 || estimates.length > 0;
      const workspaceTitle = entityData?.title
        || (entityData?.workspace_number ? `Workspace #${entityData.workspace_number}` : 'Workspace');

      return (
        <Card size="small" title="Workspace" loading={entityLoading}>
          {entityData ? (
            <Space orientation="vertical" size="small" style={{ width: '100%' }}>
              <Text strong style={{ fontSize: 13 }}>{workspaceTitle}</Text>

              {hasLinked ? (
                <>
                  {invoices.map((inv) => (
                    <Link key={inv.id} to={`/invoices/${inv.id}`} style={{ display: 'block' }}>
                      <Button
                        className="p-0"
                        icon={<FileTextOutlined />}
                        type="link"
                        size="small"
                      >
                        <Text strong style={{ fontSize: 13 }}>
                          Invoice{inv.sale_number ? ` #${inv.sale_number}` : ''}
                        </Text>
                      </Button>
                    </Link>
                  ))}
                  {estimates.map((est) => (
                    <Link key={est.id} to={`/estimates/${est.id}`} style={{ display: 'block' }}>
                      <Button
                        className="p-0"
                        icon={<FileDoneOutlined />}
                        type="link"
                        size="small"
                      >
                        <Text strong style={{ fontSize: 13 }}>
                          Estimate{est.estimate_number ? ` #${est.estimate_number}` : ''}
                        </Text>
                      </Button>
                    </Link>
                  ))}
                </>
              ) : (
                <Text type="secondary" style={{ fontSize: 13 }}>
                  No invoices or estimates linked yet.
                </Text>
              )}
            </Space>
          ) : (
            <Text type="secondary">—</Text>
          )}
        </Card>
      );
    }

    const documentNumber = entityData
      ? (entityData.number ?? entityData.sale_number ?? entityData.estimate_number ?? null)
      : null;
    const documentDetailPath = variant === 'invoice' ? `/invoices/${publicId}` : `/estimates/${publicId}`;

    return (
      <Card size="small" title={documentLabel} loading={entityLoading}>
        {entityData ? (
          <Link to={documentDetailPath} style={{ display: 'block' }}>
            <Button
              className="p-0"
              icon={<FiArrowRight />}
              iconPlacement="end"
              type="link"
              size="small"
            >
              <Text strong style={{ fontSize: 13 }}>
                {documentLabel}
                {documentNumber ? ` #${documentNumber}` : ''}
              </Text>
            </Button>
          </Link>
        ) : (
          <Text type="secondary">—</Text>
        )}
      </Card>
    );
  }, [variant, documentLabel, entityLoading, entityData, publicId]);

  const loadFeed = useCallback(() => {
    if (variant === 'invoice')  return customerService.getInvoiceWorkspaceFeed(publicId);
    if (variant === 'estimate') return customerService.getEstimateWorkspaceFeed(publicId);
    if (variant === 'workspace') return customerService.getWorkspaceFeed(publicId);
    return Promise.resolve({ data: { data: { feed: [] } } });
  }, [variant, publicId]);

  const postWorkspaceEvent = useCallback(
    (formData) => {
      if (variant === 'invoice')   return customerService.postInvoiceEvent(publicId, formData);
      if (variant === 'estimate')  return customerService.postEstimateEvent(publicId, formData);
      if (variant === 'workspace') return customerService.postWorkspaceEvent(publicId, formData);
      return Promise.reject(new Error('Posting not supported for this view.'));
    },
    [variant, publicId],
  );

  const postWorkspaceEventReply = useCallback(
    (payload) => {
      const fd = new FormData();
      fd.append('workspace_event_public_id', payload.workspaceEventPublicId);
      if (payload.body) fd.append('body', payload.body);
      (payload.files || []).forEach((f) => fd.append('files[]', f));
      if (variant === 'invoice')   return customerService.postInvoiceEventReply(publicId, fd);
      if (variant === 'estimate')  return customerService.postEstimateEventReply(publicId, fd);
      if (variant === 'workspace') return customerService.postWorkspaceEventReply(publicId, fd);
      return Promise.reject(new Error('Replying not supported for this view.'));
    },
    [variant, publicId],
  );

  const isWorkspace = variant === 'workspace';

  const workspaceParticipants = isWorkspace && Array.isArray(entityData?.participants)
    ? entityData.participants
    : null;

  const workspaceEventsConfig = useMemo(
    () => ({
      canUpload: true,
      canComment: true,
      showCustomerVisibilityToggle: false,
      emptyStateDescription: isWorkspace
        ? 'No messages yet.'
        : 'No messages yet. Upload files or leave a note for the business here.',
      loadFeed,
      portalHandlers: { postWorkspaceEvent, postWorkspaceEventReply },
      portalWorkspace: { summarySlot, participants: workspaceParticipants },
    }),
    [isWorkspace, loadFeed, postWorkspaceEvent, postWorkspaceEventReply, summarySlot, workspaceParticipants],
  );

  const backPath = '/workspaces';

  return (
    <PageShell>
      <Flex align="center" gap={12} style={{ marginBottom: 20, flexWrap: 'wrap' }}>
        <Button
          variant="filled"
          color="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(backPath)}
        >
          Workspaces
        </Button>
        <div
          style={{
            width: 1,
            height: 20,
            background: 'var(--varexe-border-color)',
          }}
          aria-hidden
        />
      </Flex>

      <WorkspaceEventsPanel
        entityPublicId={publicId}
        documentType={documentType}
        panelLayout="timeline"
        subscribeWorkspaceEvent={workspaceEventSubscribeNoOp}
        config={workspaceEventsConfig}
      />
    </PageShell>
  );
};

export default WorkspaceDetailPage;
